<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  items: { type: Array, default: () => [] },
  focusedIndex: { type: Number, default: 0 },
  itemWidth: { type: Number, default: 430 },
  itemGap: { type: Number, default: 16 },
});

// // TvP08ContentRow setup functions

const rowRef = ref(null);
const trackRef = ref(null);
const rowWidth = ref(0);
const trackWidth = ref(0);
let resizeObserver = null;

const measure = () => {
  if (rowRef.value) rowWidth.value = rowRef.value.offsetWidth;
  if (trackRef.value) trackWidth.value = trackRef.value.scrollWidth;
};

const itemPitch = computed(() => props.itemWidth + props.itemGap);

// // Roaming-focus behavior (matches the TVP8 Focus Row reference): while there are still
// // off-screen items to reveal, the row scrolls so the focused item always lands in the
// // leftmost fully-visible slot. Once the final item is fully on screen, the row stops
// // scrolling — focus then moves freely within the remaining visible items.
// //
// // maxScrollPx is measured from the actual rendered track/row widths rather than derived from
// // an assumed "how many items fit" count — that assumption leaves leftover slack whenever the
// // items don't divide the row's width evenly, which throws off the flush-right alignment once
// // fully scrolled (the last item ends up short of the true right edge, unlike the flush-left
// // alignment at the start).
const maxScrollPx = computed(() => Math.max(0, trackWidth.value - rowWidth.value));

const scrollOffset = computed(() => {
  const desiredOffset = props.focusedIndex * itemPitch.value;
  return -Math.min(desiredOffset, maxScrollPx.value);
});

// // TvP08ContentRow handler functions

// // TvP08ContentRow watchers

// // TvP08ContentRow lifecycle hooks

onMounted(() => {
  measure();
  resizeObserver = new ResizeObserver(() => measure());
  if (rowRef.value) resizeObserver.observe(rowRef.value);
  if (trackRef.value) resizeObserver.observe(trackRef.value);
});

onUnmounted(() => {
  resizeObserver?.disconnect();
});
</script>

<template>
  <div ref="rowRef" class="tvp-08-content-row">
    <div
      ref="trackRef"
      class="tvp-08-content-row__track"
      :style="{ transform: `translateX(${scrollOffset}px)` }"
    >
      <div
        v-for="(item, index) in items"
        :key="item.key ?? index"
        class="tvp-08-content-row__item"
        :style="{ width: `${itemWidth}px`, marginRight: `${itemGap}px` }"
      >
        <slot :item="item" :index="index" :is-focused="index === focusedIndex" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use './tvp08contentrow.scss';
</style>
